import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, User, ArrowLeft, MessageCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { waLink } from "@/lib/config";
import Seo from "@/components/Seo";

export default function Article() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="article-notfound" className="pt-40 pb-24 min-h-screen bg-brand-slate text-center px-5">
        <h1 className="font-heading font-extrabold text-4xl text-white">Article not found</h1>
        <p className="mt-3 text-slate-400">The post you're looking for doesn't exist.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 font-semibold text-white">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-40 pb-24 min-h-screen bg-brand-slate">
        <div className="mx-auto max-w-3xl px-5 animate-pulse space-y-4">
          <div className="h-6 w-1/4 bg-white/10 rounded" />
          <div className="h-10 w-3/4 bg-white/10 rounded" />
          <div className="h-72 w-full bg-white/5 rounded-2xl" />
        </div>
      </div>
    );
  }

  const date = new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    author: { "@type": "Organization", name: post.author },
    publisher: { "@type": "Organization", name: "Digitify My Biz" },
    datePublished: post.created_at,
    dateModified: post.updated_at,
    articleSection: post.category,
  };

  return (
    <article data-testid="article-page" className="pt-32 pb-24 min-h-screen bg-brand-slate">
      <Seo schema={articleSchema} id="article-jsonld" />
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Link to="/blog" data-testid="article-back" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-brand-cyan transition-colors">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <div className="mt-6">
          <span className="rounded-full bg-brand-blue px-3 py-1 text-xs font-semibold text-white">{post.category}</span>
          <h1 className="mt-5 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl leading-tight">
            {post.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-slate-400">
            <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {post.author}</span>
            <span>·</span>
            <span>{date}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.read_time} min read</span>
          </div>
        </div>

        <div className="mt-8 rounded-3xl overflow-hidden border border-white/10">
          <img src={post.cover_image} alt={post.title} className="w-full h-72 sm:h-96 object-cover" />
        </div>

        <div className="article-prose mt-10" data-testid="article-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        {/* Inline CTA */}
        <div className="mt-14 relative overflow-hidden rounded-3xl border border-brand-cyan/20 bg-brand-navy p-8 text-center cyan-glow">
          <div className="absolute inset-0 hero-spotlight opacity-60" />
          <div className="relative">
            <h3 className="font-heading font-extrabold text-2xl text-white">Want this system built for you?</h3>
            <p className="mt-2 text-slate-300">Test our live WhatsApp bot or talk to a strategist now.</p>
            <a
              href={waLink("Hi Digitify My Biz! I just read your article and want to grow my real estate business.")}
              target="_blank"
              rel="noreferrer"
              data-testid="article-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-whatsapp px-6 py-3.5 font-semibold text-brand-slate transition-transform hover:scale-[1.03] active:scale-95"
            >
              <MessageCircle className="h-4 w-4" /> Chat with us <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
